import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import KeyIcon from "@mui/icons-material/VpnKeyOutlined"
import CopyIcon from "@mui/icons-material/ContentCopyOutlined"
import RevealIcon from "@mui/icons-material/VisibilityOutlined"
import HideIcon from "@mui/icons-material/VisibilityOffOutlined"
import EditIcon from "@mui/icons-material/EditOutlined"
import ShareIcon from "@mui/icons-material/PersonAddAlt1Outlined"
import TrashIcon from "@mui/icons-material/DeleteOutlineOutlined"
import OpenIcon from "@mui/icons-material/OpenInNewOutlined"
import ShieldIcon from "@mui/icons-material/GppGoodOutlined"
import WarningIcon from "@mui/icons-material/WarningAmberOutlined"
import HistoryIcon from "@mui/icons-material/HistoryOutlined"

import { clientApi, vaultApi } from "../api"
import { usePermissions } from "../permissions"
import { useUndo } from "../context/UndoContext"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import Figure from "../components/Figure"
import { LoadingRows } from "../components/LoadingState"
import ClientSelect from "../components/ClientSelect"
import UserAvatar from "../components/UserAvatar"
import { formatDateTime, relativeTime } from "../utils/format"
import { rise } from "../motion"
import VaultItemDialog from "./vault/VaultItemDialog"
import VaultShareDialog from "./vault/VaultShareDialog"

/**
 * The password vault.
 *
 * ── What it is up against ───────────────────────────────────────────────────
 *
 * Not an attacker. A spreadsheet called passwords.xlsx, a chat thread from 2024
 * with eleven participants in it, and the carrier portal login that three
 * people know and nobody wrote down. The failure this prevents is almost never
 * cryptographic — it is that somebody left and nobody can say what they had.
 *
 * ── Why the page says where the line is ─────────────────────────────────────
 *
 * Secrets are sealed on the server, not in the browser (see the backend's
 * utils/vaultCrypto.js, which argues the trade at length). Anybody who says
 * "encrypted" and leaves it there is inviting somebody to store something here
 * that should never have left their own head, so the page says it plainly, once,
 * where it will be read.
 *
 * ── Why revealing is a deliberate act ───────────────────────────────────────
 *
 * Every reveal is written down, and the row says so before it happens. A vault
 * whose reads leave no trace is a vault where a leak has no origin.
 */

const STRENGTH = [
  { label: "Very weak", color: "error" },
  { label: "Weak", color: "error" },
  { label: "Fair", color: "warning" },
  { label: "Strong", color: "success" },
  { label: "Very strong", color: "success" },
]

export default function VaultPage() {
  const { can, isBackOffice } = usePermissions()
  const { offer } = useUndo()

  const [clientId, setClientId] = useState("")
  const [clients, setClients] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("")
  const [tab, setTab] = useState("entries")
  const [audit, setAudit] = useState(null)
  const [editing, setEditing] = useState(null)
  const [sharing, setSharing] = useState(null)
  /* One revealed at a time, held here rather than on the row: a page that
     remembers six of them is six secrets sitting in memory because somebody
     went to lunch mid-task. */
  const [revealed, setRevealed] = useState(null)

  const scope = useMemo(() => (clientId ? { client_id: clientId } : {}), [clientId])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const payload = await vaultApi.list({ ...scope, q: search.trim() || undefined, category: category || undefined })
      setData(payload)
      setError(null)
    } catch (err) {
      setError(err.userMessage || "The vault could not be opened.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [scope, search, category])

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  useEffect(() => {
    if (!isBackOffice || !can("clients", "view")) return
    clientApi
      .list()
      .then((payload) => setClients(payload.organizations || []))
      .catch(() => setClients([]))
  }, [isBackOffice, can])

  useEffect(() => {
    if (tab !== "audit" || !can("vault", "view_audit")) return
    setAudit(null)
    vaultApi
      .audit(scope)
      .then((payload) => setAudit(payload.entries || []))
      .catch(() => setAudit([]))
  }, [tab, scope, can])

  /* The reveal drops itself after a minute whatever else happens. Somebody
     walks away mid-task far more often than somebody is shoulder-surfed, and
     both are answered by the secret not being on the screen. */
  useEffect(() => {
    if (!revealed) return undefined
    const timer = setTimeout(() => setRevealed(null), 60000)
    return () => clearTimeout(timer)
  }, [revealed])

  const reveal = async (item) => {
    if (revealed?.id === item.id) {
      setRevealed(null)
      return
    }
    try {
      const payload = await vaultApi.reveal(item.id, scope)
      setRevealed({ id: item.id, secret: payload.secret, notes: payload.notes })
    } catch (err) {
      setError(err.userMessage || "That secret could not be read.")
    }
  }

  /**
   * Copy, and take it back out of the clipboard afterwards.
   *
   * A password left on the clipboard is one paste away from a chat box for the
   * rest of the afternoon. Clearing it is best-effort — a browser can refuse
   * the write when the tab is not focused — so the message says what happened
   * rather than promising it.
   */
  const copy = async (item) => {
    try {
      const secret = revealed?.id === item.id ? revealed.secret : (await vaultApi.reveal(item.id, scope)).secret
      await navigator.clipboard.writeText(secret)
      setNotice(`"${item.name}" is on the clipboard. It will be cleared in 30 seconds.`)
      setTimeout(() => {
        navigator.clipboard.writeText("").catch(() => {})
      }, 30000)
    } catch (err) {
      setError(err.userMessage || "That could not be copied.")
    }
  }

  const remove = async (item) => {
    try {
      await vaultApi.remove(item.id, scope)
      load()
      offer(`"${item.name}" moved to the trash`, async () => {
        await vaultApi.restore(item.id, scope)
        load()
      })
    } catch (err) {
      setError(err.userMessage || "That entry could not be removed.")
    }
  }

  const items = data?.items || []
  const weak = items.filter((item) => item.strength != null && item.strength <= 1).length
  const reused = items.filter((item) => item.reused).length
  const stale = items.filter(
    (item) => item.rotated_at && Date.now() - new Date(item.rotated_at).getTime() > 365 * 24 * 3600 * 1000,
  ).length

  const notConfigured = error && String(error).toLowerCase().includes("not configured")

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Password Vault"
        subtitle="Shared logins, kept somewhere they can be handed over and taken back."
        actions={
          can("vault", "create") ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ mode: "create" })}>
              Add an entry
            </Button>
          ) : null
        }
      />

      {notConfigured ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The vault has no encryption key on this deployment, so it is switched off rather than storing anything
          unprotected. Set <code>VAULT_KEY</code> to 32 random bytes to turn it on.
        </Alert>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      ) : null}

      {/* Said once, at the top, in the words somebody would use to decide what
          to put in here. */}
      <Alert severity="info" icon={<ShieldIcon />} sx={{ mb: 2 }}>
        Entries are encrypted on our servers and every reveal is recorded against your name. This is not end-to-end
        encryption — an administrator with the deployment key could read them — so it is the right place for shared
        operational logins and the wrong place for anything personal.
      </Alert>

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="entries" label={`Entries${items.length ? ` (${items.length})` : ""}`} />
        {can("vault", "view_audit") ? <Tab value="audit" label="Who read what" /> : null}
      </Tabs>

      {tab === "entries" ? (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {isBackOffice && clients.length > 0 ? (
                  <ClientSelect
                    clients={clients}
                    value={clientId}
                    onChange={setClientId}
                    emptyLabel="Our own vault"
                    label="Vault"
                    sx={{ minWidth: 200 }}
                  />
                ) : null}
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Name, username or address"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ minWidth: 240 }}
                />
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip
                    size="small"
                    label="All"
                    color={category ? "default" : "primary"}
                    variant={category ? "outlined" : "filled"}
                    onClick={() => setCategory("")}
                  />
                  {(data?.categories || []).map((row) => (
                    <Chip
                      key={row.name}
                      size="small"
                      label={`${row.name} (${row.count})`}
                      color={category === row.name ? "primary" : "default"}
                      variant={category === row.name ? "filled" : "outlined"}
                      onClick={() => setCategory(category === row.name ? "" : row.name)}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {items.length > 0 ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
              <Figure icon={KeyIcon} label="Entries" value={String(items.length)} caption="you can see" />
              <Figure
                icon={WarningIcon}
                label="Weak"
                value={String(weak)}
                caption={weak ? "worth changing today" : "nothing obviously guessable"}
              />
              <Figure
                icon={WarningIcon}
                label="Reused"
                value={String(reused)}
                caption={reused ? "the same password more than once" : "every one is different"}
              />
              <Figure
                icon={HistoryIcon}
                label="Over a year old"
                value={String(stale)}
                caption={stale ? "not changed since last year" : "all changed within the year"}
              />
            </Stack>
          ) : null}

          {loading ? (
            <Card>
              <CardContent>
                <LoadingRows rows={4} />
              </CardContent>
            </Card>
          ) : items.length === 0 ? (
            <Card>
              <EmptyState
                icon={KeyIcon}
                title={search || category ? "Nothing matches" : "The vault is empty"}
                description={
                  search || category
                    ? "Clear the search or the category to see everything you can."
                    : "Put the first shared login in — the carrier portal, the bank, the thing three people know and nobody wrote down."
                }
                actionLabel={can("vault", "create") && !search && !category ? "Add an entry" : undefined}
                onAction={can("vault", "create") ? () => setEditing({ mode: "create" }) : undefined}
              />
            </Card>
          ) : (
            <Card sx={{ ...rise() }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entry</TableCell>
                    <TableCell sx={{ width: 190 }}>Username</TableCell>
                    <TableCell sx={{ width: 150 }}>Strength</TableCell>
                    <TableCell sx={{ width: 130 }}>Changed</TableCell>
                    <TableCell sx={{ width: 170 }} align="right">
                      Password
                    </TableCell>
                    <TableCell sx={{ width: 120 }} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {item.name}
                              </Typography>
                              {item.url ? (
                                <Tooltip title={item.url}>
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={item.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    aria-label={`Open ${item.name} in a new tab`}
                                    sx={{ p: 0.25 }}
                                  >
                                    <OpenIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {item.category}
                              </Typography>
                              {item.access === "shared" ? (
                                <Chip size="small" label="Shared with you" variant="outlined" sx={{ height: 17, fontSize: 9 }} />
                              ) : null}
                              {item.share_count > 0 ? (
                                <Tooltip title={`Shared with ${item.share_count}`}>
                                  <Chip
                                    size="small"
                                    label={`${item.share_count} other${item.share_count === 1 ? "" : "s"}`}
                                    variant="outlined"
                                    sx={{ height: 17, fontSize: 9 }}
                                  />
                                </Tooltip>
                              ) : null}
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                            {item.username || "—"}
                          </Typography>
                          {item.username ? (
                            <Tooltip title="Copy the username">
                              <IconButton
                                size="small"
                                sx={{ p: 0.25 }}
                                aria-label={`Copy the username for ${item.name}`}
                                onClick={() => {
                                  navigator.clipboard.writeText(item.username).catch(() => {})
                                  setNotice("Username copied.")
                                }}
                              >
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Strength score={item.strength} reused={item.reused} />
                      </TableCell>

                      <TableCell>
                        <Tooltip title={item.rotated_at ? formatDateTime(item.rotated_at) : "Never changed"}>
                          <Typography variant="caption" color="text.secondary">
                            {item.rotated_at ? relativeTime(item.rotated_at) : "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">
                        {revealed?.id === item.id ? (
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", wordBreak: "break-all", textAlign: "right" }}
                          >
                            {revealed.secret}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ letterSpacing: 2 }}>
                            ••••••••
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          {data?.can_reveal ? (
                            <Tooltip
                              title={
                                revealed?.id === item.id
                                  ? "Hide it again"
                                  : "Show the password — this is recorded against your name"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => reveal(item)}
                                aria-label={`${revealed?.id === item.id ? "Hide" : "Reveal"} the password for ${item.name}`}
                              >
                                {revealed?.id === item.id ? (
                                  <HideIcon fontSize="small" />
                                ) : (
                                  <RevealIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {data?.can_reveal ? (
                            <Tooltip title="Copy it, and clear the clipboard afterwards">
                              <IconButton size="small" onClick={() => copy(item)} aria-label={`Copy the password for ${item.name}`}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {can("vault", "update") && item.access === "own" ? (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setEditing({ mode: "edit", item })}
                                aria-label={`Edit ${item.name}`}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {can("vault", "share") && item.access === "own" ? (
                            <Tooltip title="Share">
                              <IconButton size="small" onClick={() => setSharing(item)} aria-label={`Share ${item.name}`}>
                                <ShareIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {can("vault", "delete") && item.access === "own" ? (
                            <Tooltip title="Move to trash">
                              <IconButton size="small" onClick={() => remove(item)} aria-label={`Delete ${item.name}`}>
                                <TrashIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {revealed?.notes ? (
                <>
                  <Divider />
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                      Notes on the entry you revealed
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
                      {revealed.notes}
                    </Typography>
                  </CardContent>
                </>
              ) : null}
            </Card>
          )}
        </>
      ) : (
        <Card>
          {audit === null ? (
            <CardContent>
              <LoadingRows rows={5} />
            </CardContent>
          ) : audit.length === 0 ? (
            <EmptyState icon={HistoryIcon} title="Nothing recorded yet" description="Every reveal, change and share turns up here." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 190 }}>Who</TableCell>
                  <TableCell sx={{ width: 120 }}>Did what</TableCell>
                  <TableCell>To which entry</TableCell>
                  <TableCell sx={{ width: 150 }}>When</TableCell>
                  <TableCell sx={{ width: 120 }}>From</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audit.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <UserAvatar name={row.actor_name} avatarKey={row.avatar_key} size={22} tooltip={false} />
                        <Typography variant="caption">{row.actor_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.action}
                        color={row.action === "revealed" ? "warning" : row.action === "refused" ? "error" : "default"}
                        variant={row.action === "revealed" || row.action === "refused" ? "filled" : "outlined"}
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {row.item_name || `Entry ${row.item_id ?? "—"}`}
                      </Typography>
                      {row.detail ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.detail}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(row.created_at)}>
                        <Typography variant="caption" color="text.secondary">
                          {relativeTime(row.created_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.ip_address || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      <VaultItemDialog
        state={editing}
        clientId={clientId}
        onClose={() => setEditing(null)}
        onSaved={(message) => {
          setEditing(null)
          setNotice(message)
          load()
        }}
      />
      <VaultShareDialog item={sharing} clientId={clientId} onClose={() => setSharing(null)} onChanged={load} />
    </Box>
  )
}

/**
 * How strong it is, without decrypting anything.
 *
 * Scored on the server when the secret was set and stored as a number, so a
 * list of forty entries can warn about the two weak ones without forty
 * decryptions to draw a table.
 */
function Strength({ score, reused }) {
  if (score == null) return <Typography variant="caption" color="text.disabled">—</Typography>
  const band = STRENGTH[Math.max(0, Math.min(4, score))]
  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <LinearProgress
          variant="determinate"
          value={((score + 1) / 5) * 100}
          color={band.color}
          sx={{ width: 54, height: 5, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary">
          {band.label}
        </Typography>
      </Stack>
      {reused ? (
        <Tooltip title="The same password is on more than one entry here. One leak would be several.">
          <Typography variant="caption" color="warning.dark" sx={{ fontSize: 10 }}>
            also used elsewhere
          </Typography>
        </Tooltip>
      ) : null}
    </Stack>
  )
}
